import { useNavigate } from 'react-router-dom';
import { PRICES_PATH } from 'services/integrations';

// Shared bits that keep "Mes prix" (the account prices) in sight wherever a price shows up.

type IntegrationsTab = 'integrations' | 'prices' | 'themes';

/** "Intégrations · Mes prix · Mes thèmes" — the tabs at the top of the integrations area. */
export const IntegrationsTabs = ({ active, unpriced = 0 }: { active: IntegrationsTab; unpriced?: number }) => {
    const navigate = useNavigate();
    const tab = (key: IntegrationsTab, label: React.ReactNode, path: string) => (
        <button
            onClick={() => navigate(path)}
            aria-current={active === key ? 'page' : undefined}
            className={`relative flex items-center gap-2 px-1 pb-2.5 font-hanken text-[15px] font-semibold transition-colors ${
                active === key ? 'text-[#241C5C]' : 'text-[#8A8172] hover:text-[#3B352C]'
            }`}
        >
            {label}
            {active === key && <span className="absolute bottom-[-1px] left-0 right-0 h-0.5 rounded-full bg-[#241C5C]" />}
        </button>
    );
    return (
        <nav className="flex gap-6 border-b border-[#EDE7DE]" aria-label="Intégrations">
            {tab('integrations', 'Mes intégrations', '/integrations')}
            {tab(
                'prices',
                <>
                    Mes prix
                    {unpriced > 0 && (
                        <span className="rounded-full bg-[#FCF2D4] px-2 py-[1px] text-[11.5px] font-semibold text-[#7A5A05]">{unpriced} sans prix</span>
                    )}
                </>,
                PRICES_PATH
            )}
            {tab('themes', 'Mes thèmes', '/integrations/themes')}
        </nav>
    );
};

/**
 * Where a price comes from, always with the way to change it:
 * - 'account': the account price (Mes prix);
 * - 'integration': this integration's own price, with the account price it overrides;
 * - null: no price yet.
 */
export const PriceOrigin = ({
    source,
    accountPrice,
    onUseAccountPrice,
}: {
    source: 'integration' | 'account' | null;
    accountPrice?: number | null;
    onUseAccountPrice?: () => void;
}) => {
    const navigate = useNavigate();
    const link = (label: string, onClick: () => void) => (
        <button onClick={onClick} className="font-semibold text-[#241C5C] hover:underline">
            {label}
        </button>
    );
    return (
        <span className="font-hanken text-[12px] text-[#8A8172]">
            {source === 'account' ? (
                <>Prix du compte · {link('Modifier dans Mes prix', () => navigate(PRICES_PATH))}</>
            ) : source === 'integration' ? (
                <>
                    Prix de cette intégration
                    {accountPrice != null ? ` (compte : ${accountPrice} €)` : ''}
                    {onUseAccountPrice && accountPrice != null && <> · {link('Revenir au prix du compte', onUseAccountPrice)}</>}
                </>
            ) : (
                <>Pas encore de prix · les prix de {link('Mes prix', () => navigate(PRICES_PATH))} sont proposés par défaut</>
            )}
        </span>
    );
};
